import { getApiBaseUrl, normalizeApiEndpoint } from '../config';
import { rewriteMediaUrlForDisplay } from '../utils/mediaUrl';
import {
  API_AUTH_REQUIRED_MESSAGE,
  API_BATCH_UPLOAD_ABORTED_MESSAGE,
  API_BATCH_UPLOAD_FAILED_MESSAGE,
  API_BATCH_UPLOAD_NETWORK_ERROR_MESSAGE,
  API_INVALID_REFRESH_RESPONSE_MESSAGE,
  API_PARSE_RESPONSE_FAILED_MESSAGE,
  API_REQUEST_FAILED_RETRY_MESSAGE,
  API_SESSION_EXPIRED_LOGIN_AGAIN_MESSAGE,
  API_SESSION_EXPIRED_MESSAGE,
  API_TOKEN_REFRESH_FAILED_MESSAGE,
  API_UPLOAD_ABORTED_MESSAGE,
  API_UPLOAD_FAILED_MESSAGE,
  API_UPLOAD_NETWORK_ERROR_MESSAGE,
  API_UPLOAD_TIMEOUT_MANY_FILES_MESSAGE,
  API_UPLOAD_TIMEOUT_MESSAGE,
  GENERIC_API_FAILED_AFTER_REFRESH_MESSAGE,
  GENERIC_API_FAILED_MESSAGE,
} from '../utils/errorMessage';
function normalizeApiBaseUrl(rawBase) {
  const trimmed = (rawBase || '').trim();
  if (trimmed === '') return '';
  const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return withProto.replace(/\/$/, '');
}

class ApiService {
  constructor() {
    this.token = null;  // Access token in memory only (no localStorage)
    this.isRefreshing = false;
    this.failedQueue = [];
    this.refreshPromise = null;
  }

  getToken() {
    return this.token;
  }

  setAuthToken(token) {
    this.token = token;
    // No localStorage - token lives only in memory to reduce XSS impact
  }

  /**
   * Returns true if access token is missing or expired (JWT exp claim).
   * Uses 30s buffer to avoid sending requests that would get 401.
   * Used to prevent polling when session is expired (avoids 401 logs in console).
   */
  isTokenExpired() {
    const token = this.token;
    if (!token || typeof token !== 'string') return true;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      const exp = decoded.exp;
      if (typeof exp !== 'number') return false; // no exp, assume valid
      const now = Math.floor(Date.now() / 1000);
      const bufferSeconds = 30;
      return exp < now + bufferSeconds;
    } catch {
      return true; // invalid token, treat as expired
    }
  }

  // Process failed requests queue
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  // Secure token refresh using httpOnly cookies.
  // Only 401 from refresh means "session really expired" and triggers logout (tokenExpired).
  // Network/5xx errors do not trigger logout so the user can retry (e.g. after checkout).
  // On 401 we retry once after a short delay: after redirect from Stripe/payment, the browser
  // sometimes doesn't send the cross-site cookie on the first request.
  async refreshToken() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }
    this.isRefreshing = true;
    this.refreshPromise = this._doRefreshToken();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async _doRefreshToken() {
    const refreshOpts = {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    };
    const refreshUrl = `${this.baseURL}${normalizeApiEndpoint('/api/accounts/refresh/')}`;
    try {
      let response = await fetch(refreshUrl, refreshOpts);
      if (!response.ok && response.status === 401) {
        // Retry once after delay (cookie may not be sent on first request after redirect)
        await new Promise((r) => setTimeout(r, 450));
        response = await fetch(refreshUrl, refreshOpts);
      }
      if (!response.ok) {
        if (response.status !== 401) {
          console.error('🔄 Token refresh failed:', response.status, response.statusText);
        }
        this.setAuthToken(null);
        const err = new Error(
          response.status === 401 ? API_SESSION_EXPIRED_MESSAGE : API_TOKEN_REFRESH_FAILED_MESSAGE,
        );
        err.status = response.status;
        throw err;
      }
      const data = await response.json();
      const accessToken = data?.access_token;
      if (!accessToken) {
        this.setAuthToken(null);
        const err = new Error(API_INVALID_REFRESH_RESPONSE_MESSAGE);
        err.status = 502;
        throw err;
      }
      this.setAuthToken(accessToken);
      this.processQueue(null, accessToken);
      return { accessToken, rememberMe: Boolean(data.remember_me) };
    } catch (error) {
      this.processQueue(error, null);
      this.setAuthToken(null);
      // Only treat 401 as "session expired" and trigger global logout; other errors are temporary
      if (error?.status === 401) {
        window.dispatchEvent(new CustomEvent('tokenExpired'));
      }
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async postWithProgress(endpoint, data, onProgress) {
    /**
     * Upload file with progress tracking using XMLHttpRequest
     * @param {string} endpoint - API endpoint
     * @param {FormData} data - FormData with file
     * @param {function} onProgress - Callback with (loaded, total, percentage)
     * @returns {Promise} Response data
     */
    return new Promise((resolve, reject) => {
      const path = normalizeApiEndpoint(endpoint);
      const url = `${this.baseURL}${path}`;
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentage = (e.loaded / e.total) * 100;
          onProgress(e.loaded, e.total, percentage);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const contentType = xhr.getResponseHeader('content-type') || '';
            if (contentType.includes('application/json')) {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } else {
              resolve(xhr.responseText);
            }
          } catch (error) {
            reject(new Error(API_PARSE_RESPONSE_FAILED_MESSAGE));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            const summary =
              (typeof errorData.message === 'string' && errorData.message) ||
              (typeof errorData.error === 'string' && errorData.error) ||
              API_UPLOAD_FAILED_MESSAGE;
            const error = new Error(summary);
            error.status = xhr.status;
            error.data = errorData;
            reject(error);
          } catch {
            const error = new Error(API_UPLOAD_FAILED_MESSAGE);
            error.status = xhr.status;
            reject(error);
          }
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error(API_UPLOAD_NETWORK_ERROR_MESSAGE));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error(API_UPLOAD_ABORTED_MESSAGE));
      });

      // Open and send request
      xhr.open('POST', url);
      
      // Set headers
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }
      // Don't set Content-Type for FormData - browser will set it with boundary

      // Include credentials
      xhr.withCredentials = true;

      xhr.send(data);
    });
  }

  postBatchWithProgress(endpoint, formData, onProgress, onFileProgress) {
    /**
     * Upload multiple files in batch with progress tracking
     * @param {string} endpoint - API endpoint
     * @param {FormData} formData - FormData with multiple 'audio_files'
     * @param {function} onProgress - Callback with (loaded, total, percentage) for total progress
     * @param {function} onFileProgress - Callback with (fileName, loaded, total, percentage) for per-file progress
     * @returns {Promise} Response data
     */
    return new Promise((resolve, reject) => {
      const path = normalizeApiEndpoint(endpoint);
      const url = `${this.baseURL}${path}`;
      const xhr = new XMLHttpRequest();

      // Calculate total size for progress simulation
      const files = formData.getAll('audio_files');
      const _totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
      let _uploadedSoFar = 0;

      // Set up progress tracking for total upload
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          _uploadedSoFar = e.loaded;
          const percentage = (e.loaded / e.total) * 100;
          
          // Call total progress callback
          if (onProgress) {
            onProgress(e.loaded, e.total, percentage);
          }

          // Per-file progress: all files in batch share the same % (all bars move together)
          if (onFileProgress && files.length > 0) {
            const percentage = (e.loaded / e.total) * 100;
            const fileProgress = Math.min(percentage, 100);
            files.forEach((file) => {
              const fileLoaded = (file.size * fileProgress) / 100;
              onFileProgress(file.name, fileLoaded, file.size, fileProgress);
            });
          }
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const contentType = xhr.getResponseHeader('content-type') || '';
            if (contentType.includes('application/json')) {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } else {
              resolve(xhr.responseText);
            }
          } catch (error) {
            reject(new Error(API_PARSE_RESPONSE_FAILED_MESSAGE));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            const summary =
              (typeof errorData.message === 'string' && errorData.message) ||
              (typeof errorData.error === 'string' && errorData.error) ||
              API_BATCH_UPLOAD_FAILED_MESSAGE;
            const error = new Error(summary);
            error.status = xhr.status;
            error.data = errorData;
            reject(error);
          } catch {
            const error = new Error(API_BATCH_UPLOAD_FAILED_MESSAGE);
            error.status = xhr.status;
            reject(error);
          }
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error(API_BATCH_UPLOAD_NETWORK_ERROR_MESSAGE));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error(API_BATCH_UPLOAD_ABORTED_MESSAGE));
      });

      // Open and send request
      xhr.open('POST', url);
      
      // Set headers
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }
      // Don't set Content-Type for FormData - browser will set it with boundary

      // Include credentials
      xhr.withCredentials = true;

      // Store xhr for potential abort
      this._currentXhr = xhr;
      
      // Clear xhr reference on completion
      const clearXhr = () => {
        if (this._currentXhr === xhr) {
          this._currentXhr = null;
        }
      };
      
      xhr.addEventListener('load', clearXhr);
      xhr.addEventListener('error', clearXhr);
      xhr.addEventListener('abort', clearXhr);

      xhr.send(formData);
    });
  }
  
  // Method to abort current upload
  abortCurrentUpload() {
    if (this._currentXhr) {
      this._currentXhr.abort();
      this._currentXhr = null;
    }
  }

  /**
   * Upload single file with progress - for parallel batch uploads.
   * Each file gets its own XHR and real per-file progress.
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - FormData with single 'audio_file'
   * @param {function} onProgress - Callback with (fileName, loaded, total, percentage)
   * @returns {Promise} Response data
   */
  postSingleFileWithProgress(endpoint, formData, onProgress) {
    return new Promise((resolve, reject) => {
      const path = normalizeApiEndpoint(endpoint);
      const url = `${this.baseURL}${path}`;
      const xhr = new XMLHttpRequest();
      const file = formData.get('audio_file');
      const fileName = file?.name || 'file';

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentage = (e.loaded / e.total) * 100;
          onProgress(fileName, e.loaded, e.total, percentage);
        }
      });

      xhr.addEventListener('load', () => {
        this._activeXhrs?.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const contentType = xhr.getResponseHeader('content-type') || '';
            if (contentType.includes('application/json')) {
              resolve(JSON.parse(xhr.responseText));
            } else {
              resolve(xhr.responseText);
            }
          } catch {
            reject(new Error(API_PARSE_RESPONSE_FAILED_MESSAGE));
          }
        } else {
          if (xhr.status === 524) {
            const err = new Error(API_UPLOAD_TIMEOUT_MANY_FILES_MESSAGE);
            err.status = 524;
            err.data = { error: 'timeout' };
            reject(err);
            return;
          }
          try {
            const errorData = JSON.parse(xhr.responseText);
            const summary =
              (typeof errorData.message === 'string' && errorData.message) ||
              (typeof errorData.error === 'string' && errorData.error) ||
              API_UPLOAD_FAILED_MESSAGE;
            const err = new Error(summary);
            err.status = xhr.status;
            err.data = errorData;
            reject(err);
          } catch {
            reject(
              new Error(
                xhr.status === 0 ? API_UPLOAD_NETWORK_ERROR_MESSAGE : API_UPLOAD_FAILED_MESSAGE,
              ),
            );
          }
        }
      });

      xhr.addEventListener('error', () => {
        this._activeXhrs?.delete(xhr);
        reject(new Error(API_UPLOAD_NETWORK_ERROR_MESSAGE));
      });

      xhr.addEventListener('abort', () => {
        this._activeXhrs?.delete(xhr);
        reject(new Error(API_UPLOAD_ABORTED_MESSAGE));
      });

      xhr.addEventListener('timeout', () => {
        this._activeXhrs?.delete(xhr);
        const err = new Error(API_UPLOAD_TIMEOUT_MESSAGE);
        err.status = 408;
        err.data = { error: 'timeout' };
        reject(err);
      });

      xhr.open('POST', url);
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }
      xhr.withCredentials = true;
      xhr.timeout = 10 * 60 * 1000;

      if (!this._activeXhrs) this._activeXhrs = new Set();
      this._activeXhrs.add(xhr);

      xhr.send(formData);
    });
  }

  get baseURL() {
    return normalizeApiBaseUrl(getApiBaseUrl());
  }

  abortAllUploads() {
    if (this._activeXhrs) {
      this._activeXhrs.forEach(xhr => {
        try { xhr.abort(); } catch (_) { /* abort may throw */ }
      });
      this._activeXhrs.clear();
    }
    this.abortCurrentUpload();
  }

  async request(endpoint, options = {}) {
    const path = normalizeApiEndpoint(endpoint);
    const url = `${this.baseURL}${path}`;
    
    const config = {
      headers: {
        ...options.headers,
      },
      credentials: 'include', // Always include cookies for refresh tokens
      ...options,
    };
    
    // Only set Content-Type for JSON requests, not for FormData
    if (!(options.body instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // ALWAYS send Authorization header if user is authenticated
    // Even for AllowAny endpoints - they can still use the auth info
    // to provide personalized responses (e.g., user_can_download)
    if (this.token) {
      config.headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    let response = await fetch(url, config);
      // 425 Too Early = TLS 1.3 0-RTT rejected (e.g. returning from Stripe). Retry once.
      if (response.status === 425) {
        response = await fetch(url, config);
      }
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // Handle 429 Rate Limited - don't retry; preserve backend message for display
        if (response.status === 429) {
          const errorData = contentType.includes('application/json')
            ? await response.json().catch(() => ({}))
            : {};
          const message = errorData.message || 'Too many requests. Please wait and try again.';
          const error = new Error(message);
          error.status = 429;
          error.code =
            errorData.error_code != null
              ? errorData.error_code
              : typeof errorData.code === 'string'
                ? errorData.code
                : null;
          error.data = { error: 'rate_limited', message };
          throw error;
        }
        
        // Handle 401 Unauthorized - attempt token refresh
        if (response.status === 401) {
          const isRefreshEndpoint =
            path.includes('/accounts/refresh/') || path.includes('/auth/refresh');
          if (path.includes('/auth/login') || isRefreshEndpoint || path.includes('/auth/logout') || path.includes('/accounts/logout')) {
            this.setAuthToken(null);
            window.dispatchEvent(new CustomEvent('tokenExpired'));
            const err = new Error(API_AUTH_REQUIRED_MESSAGE);
            err.status = 401;
            throw err;
          }
          
          try {
            // Attempt to refresh token
            await this.refreshToken();
            // Retry the original request with new token
            if (this.token) {
              config.headers['Authorization'] = `Bearer ${this.token}`;
            }
            const retryResponse = await fetch(url, config);
            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              // MAINTENANCE REDIRECT (SPA – no full reload)
              if (errorData && errorData.error === 'maintenance_mode') {
                window.dispatchEvent(new CustomEvent('navigateToMaintenance'));
                return undefined;
              }
              const error = new Error(GENERIC_API_FAILED_AFTER_REFRESH_MESSAGE);
              error.status = retryResponse.status;
              error.code =
                errorData.error_code != null
                  ? errorData.error_code
                  : typeof errorData.code === 'string'
                    ? errorData.code
                    : null;
              error.data = errorData;
              throw error;
            }
            const retryData = await retryResponse.json();
            // MAINTENANCE REDIRECT (SPA – no full reload)
            if (retryData && retryData.error === 'maintenance_mode') {
              window.dispatchEvent(new CustomEvent('navigateToMaintenance'));
              return undefined;
            }
            return retryData;
          } catch (refreshError) {
            // refreshToken() already dispatched tokenExpired only when status === 401
            this.setAuthToken(null);
            const err = new Error(
              refreshError?.status === 401
                ? API_SESSION_EXPIRED_LOGIN_AGAIN_MESSAGE
                : API_REQUEST_FAILED_RETRY_MESSAGE,
            );
            err.status = refreshError?.status ?? 401;
            throw err;
          }
        }
        // Parse error response to get detailed error messages
        const errorData = contentType.includes('application/json')
          ? await response.json().catch(() => ({}))
          : {};
        // MAINTENANCE REDIRECT (skip on auth and maintenance status pages; SPA – no full reload)
        if (errorData && errorData.error === 'maintenance_mode') {
          const isAuthPage = window.location.pathname.startsWith('/auth');
          const isMaintenancePage = window.location.pathname === '/maintenance';
          if (!isAuthPage && !isMaintenancePage) {
            window.dispatchEvent(new CustomEvent('navigateToMaintenance'));
            return undefined;
          }
        }
        const error = new Error(GENERIC_API_FAILED_MESSAGE);
        error.status = response.status;
        error.code =
          errorData.error_code != null
            ? errorData.error_code
            : typeof errorData.code === 'string'
              ? errorData.code
              : null;
        error.data = errorData;
        error.response = response;
        
        // Don't log 400 errors for authentication/validation endpoints - these are validation errors
        // that are already handled and displayed to users in the UI
        const isValidationEndpoint = path.includes('/login') || 
                                     path.includes('/register') || 
                                     path.includes('/verify-email') ||
                                     path.includes('/password-reset') ||
                                     path.includes('/two-factor-auth') ||
                                     path.includes('/change-password') ||
                                     path.includes('/change-email');
        
        // Don't log 429 (rate limit) errors - these are expected and already handled in UI
        const isRateLimitError = response.status === 429;
        // Don't log 404 for public profile/user endpoints - "not found" is handled in UI
        const isNotFoundProfile =
          response.status === 404 &&
          (path.includes('/users/public/') || path.includes('/users/public/uuid/'));
        // Don't log 404 for Stripe account lookup - no account is expected pre-onboarding
        const isNotFoundStripeAccount =
          response.status === 404 &&
          path.includes('/payments/stripe-account/');
        // Don't log 404/403 for storage/me — user without upload rights (expected)
        const isNotFoundStorageMe =
          (response.status === 404 || response.status === 403) &&
          path.includes('/storage/me');
        // Only log errors that aren't expected validation/rate limit/not-found profile errors
        if (
          !(response.status === 400 && isValidationEndpoint) &&
          !isRateLimitError &&
          !isNotFoundProfile &&
          !isNotFoundStripeAccount &&
          !isNotFoundStorageMe
        ) {
          console.error('API Error:', {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            data: errorData
          });
        }
        
        throw error;
      }
      
      // Handle 204 No Content responses (like DELETE)
      if (response.status === 204) {
        return null;
      }
      
      if (!contentType.includes('application/json')) {
        // Non-JSON success (e.g., empty HTML page) → treat as empty content
        return null;
      }
      const data = await response.json();
      // MAINTENANCE REDIRECT (skip on auth and maintenance status pages; SPA – no full reload)
      if (data && data.error === 'maintenance_mode') {
        const isAuthPage = window.location.pathname.startsWith('/auth');
        const isMaintenancePage = window.location.pathname === '/maintenance';
        if (!isAuthPage && !isMaintenancePage) {
          window.dispatchEvent(new CustomEvent('navigateToMaintenance'));
          return undefined;
        }
      }
      return data;
  }

  // HTTP method shortcuts
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Authentication
  async login(email, password, code = null, rememberMe = false) {
    const body = { email, password };
    if (code) {
      body.code = code;
    }
    if (rememberMe) {
      body.remember_me = true;
    }
    
    const response = await this.request('/api/accounts/login/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    // Set access token from response (only if login successful, not if 2FA required)
    if (response.access_token) {
      this.setAuthToken(response.access_token);
    }
    
    return response;
  }

  async register(userData) {
    return await this.request('/api/accounts/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout(accessToken = null) {
    const bearer = accessToken ?? this.token;
    if (this._logoutInFlight) {
      return this._logoutInFlight;
    }
    const headers = { 'Content-Type': 'application/json' };
    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    }
    this._logoutInFlight = fetch(`${this.baseURL}${normalizeApiEndpoint('/api/accounts/logout/')}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({}),
    })
      .catch((error) => {
        console.error('Logout request failed:', error);
      })
      .finally(() => {
        this._logoutInFlight = null;
        this.setAuthToken(null);
      });
    return this._logoutInFlight;
  }

  async getCurrentUser() {
    return this.request('/api/accounts/profile/');
  }

  // Core features
  async getStreamConfig() {
    return this.request('/api/core/stream-config/');
  }

  async getFeaturedContent() {
    return this.request('/api/core/featured-content/');
  }

  async getFeaturedTracks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/featured-tracks/${query ? '?' + query : ''}`);
  }

  async getPopularTracks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/popular-tracks/${query ? '?' + query : ''}`);
  }

  // Hot/Cold votes
  async vote(contentType, uuid, value) {
    return this.post('/api/votes/', {
      content_type: contentType,
      uuid,
      value,
    });
  }

  async getHotTracks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/hot-tracks/${query ? '?' + query : ''}`);
  }

  async getColdTracks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/cold-tracks/${query ? '?' + query : ''}`);
  }

  async getHotAlbums(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/hot-albums/${query ? '?' + query : ''}`);
  }

  async getColdAlbums(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/cold-albums/${query ? '?' + query : ''}`);
  }

  async getNews(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/news/${query ? '?' + query : ''}`);
  }

  async getNewsDetail(id) {
    return this.request(`/api/core/news/${id}/`);
  }

  async getEvents(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/core/events/${query ? '?' + query : ''}`);
  }

  async getDashboardStats() {
    return this.request('/api/core/dashboard-stats/');
  }

  /** Record one engaged visit (beacon). Call once per session after 30s or first click. */
  async sendEngagementBeacon() {
    return this.request('/api/core/analytics/engagement/', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Shop features
  async getAlbums(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/albums/${query ? '?' + query : ''}`);
  }

  async getAlbumDetail(id, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/albums/${id}/${query ? '?' + query : ''}`);
  }

  async getAlbumByArtistAndSlug(username, slug, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/albums/artist/${username}/${slug}/${query ? '?' + query : ''}`);
  }

  async getFeaturedAlbums(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/shop/albums/featured/${query ? '?' + query : ''}`);
  }

  async getPopularAlbums(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/shop/albums/popular/${query ? '?' + query : ''}`);
  }

  async getNewReleases() {
    return this.request('/api/shop/albums/new-releases/');
  }

  async getGenres() {
    return this.request('/api/shop/genres/');
  }

  async calculateCartTotal(cartItems) {
    return this.request('/api/shop/cart/calculate/', {
      method: 'POST',
      body: JSON.stringify({ items: cartItems }),
    });
  }

  async getUserOrders() {
    return this.getMyOrders();
  }

  async getMyOrders() {
    return this.request('/api/orders/user/orders/');
  }

  async getOrderReceiptUrl(orderId) {
    return this.request(`/api/orders/${orderId}/receipt-url/`);
  }

  async getStripeAccount() {
    try {
      return await this.request('/api/payments/stripe-account/');
    } catch (error) {
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getStripeConfig() {
    return this.request('/api/payments/stripe-config/');
  }

  async syncStripeAccount() {
    return this.request('/api/payments/sync-stripe-account/', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async deleteStripeAccount() {
    return this.request('/api/payments/delete-stripe-account/', {
      method: 'DELETE',
    });
  }

  async getArtistSales(params = {}) {
    const query = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ''),
      ),
    ).toString();
    return this.request(`/api/orders/artist/sales/${query ? `?${query}` : ''}`);
  }

  // Payment features
  async getTransactions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/payments/transactions/${query ? '?' + query : ''}`);
  }

  async createPaymentIntent(paymentData) {
    return this.request('/api/payments/create-payment-intent/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async confirmPayment(paymentData) {
    return this.request('/api/payments/confirm-payment/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async repriceCardFee(paymentData) {
    return this.request('/api/payments/reprice-card-fee/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async createAccountLink(linkData) {
    return this.request('/api/payments/create-account-link/', {
      method: 'POST',
      body: JSON.stringify(linkData),
    });
  }

  async createLoginLink() {
    return this.request('/api/payments/create-login-link/');
  }

  async attachStripeAccount(accountId) {
    return this.request('/api/payments/connect/attach/', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    });
  }

  async cancelPaymentAuthorizations(payload) {
    return this.request('/api/payments/cancel-authorizations/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Donation features
  async getDonations(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/donations/list/${query ? '?' + query : ''}`);
  }

  async createDonationIntent(donationData) {
    return this.request('/api/donations/create-intent/', {
      method: 'POST',
      body: JSON.stringify(donationData),
    });
  }

  async confirmDonation(donationData) {
    return this.request('/api/donations/confirm/', {
      method: 'POST',
      body: JSON.stringify(donationData),
    });
  }

  // Role change
  async getRoleChangeStatus() {
    return this.request('/api/accounts/role-change-status/');
  }

  async chooseCreatorOnboardingPath() {
    return this.post('/api/accounts/onboarding/choose-creator-path/', {});
  }

  async changeUserRole(data) {
    return this.post('/api/accounts/change-role/', data);
  }

  // Username change
  async changeUsernameRequest(newUsername) {
    return this.post('/api/accounts/change-username/', { username: newUsername });
  }

  async confirmUsernameChange(token) {
    return this.post('/api/accounts/change-username/confirm/', { token });
  }

  // Email change (confirmation link from email)
  async confirmEmailChange(token) {
    return this.post('/api/accounts/change-email/confirm/', { token });
  }

  // Subscription cancellation (confirmation link from email)
  async confirmSubscriptionCancel(token) {
    return this.post('/api/donations/cancel-subscription/confirm/', { token });
  }
}

/**
 * Rewrite malformed media URLs: backend sometimes returns /media/{artist_uuid}/{track_id}/file
 * without tracks/ prefix. We never use temp for waveform (temp is deleted after callback);
 * use tracks/full/ so completed tracks load from the correct location.
 */
function ensureTracksTempPath(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, 'http://x');
    const path = u.pathname;
    // Match /media/{uuid}/{TRK-*}/{filename} but NOT already under tracks/
    const badMatch = path.match(/^\/media\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/(TRK-[A-Z0-9]+)\/(.+)$/i);
    if (badMatch && !path.includes('/tracks/')) {
      u.pathname = `/media/tracks/full/${badMatch[1]}/${badMatch[2]}/${badMatch[3]}`;
      return u.toString();
    }
  } catch (_) { /* URL parse/rewrite may throw */ }
  return url;
}

/**
 * Convert relative media URLs to absolute (for fetch/audio loading).
 * Handles /media/... and media/... formats.
 * Also fixes malformed media URLs missing tracks/full/ prefix (never rewrite to temp).
 */
export function mediaUrlToAbsolute(url) {
  if (!url || typeof url !== 'string') return url;
  let out = url;
  if (!out.startsWith('http://') && !out.startsWith('https://')) {
    const base = normalizeApiBaseUrl(getApiBaseUrl()) || (typeof window !== 'undefined' ? window.location.origin : '');
    const path = out.startsWith('/') ? out : `/${out}`;
    out = base ? `${base.replace(/\/$/, '')}${path}` : out;
  }
  return ensureTracksTempPath(out);
}

/**
 * In local dev with proxy (API_BASE_URL empty), backend may return absolute media URLs
 * (e.g. http://127.0.0.1:8000/media/...). Rewrite to relative /media/... so the request
 * goes to current origin and the dev server proxy forwards it to the backend.
 * Query string (cache-bust `v=`) is preserved.
 */
export function mediaUrlForDisplay(url) {
  return rewriteMediaUrlForDisplay(url, normalizeApiBaseUrl(getApiBaseUrl()));
}

export default new ApiService();